/**
 * Calmy e-book page.
 *
 *   1  gallery: desktop thumbnails, phone swipe, dots
 *   2  the sticky purchase bar
 *   3  the reader-preference field's validation
 *   4  the "scroll to the purchase form" CTAs
 *   5  keeping the jewellery bundle offers off this page
 *
 * SCOPED TO THIS PAGE. Every selector below is a data-ceb-* attribute that only
 * the e-book sections emit, so nothing here can bind to the jewellery product
 * page, the cart drawer or the bundles app.
 *
 * Everything is flag-guarded, so the theme editor replacing a section's markup
 * and re-running boot() over live nodes is a no-op.
 */
(function () {
  "use strict";

  var REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)");
  function smooth() { return REDUCED.matches ? "auto" : "smooth"; }

  /* ═══ 1. GALLERY ═══════════════════════════════════════════════════════════
   *
   * ONE list of slides read two ways. On a phone the track is a scroll-snap
   * row and the dots follow whichever slide is nearest the centre - correct
   * mid-swipe, not only once a snap settles. On a desktop the track does not
   * scroll and the thumbnails switch which slide is shown.
   */
  function initGallery(root) {
    if (root.__cebGal) return;
    root.__cebGal = true;

    var track = root.querySelector("[data-ceb-track]");
    if (!track) return;
    var slides = Array.prototype.slice.call(root.querySelectorAll("[data-ceb-slide]"));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll("[data-ceb-thumb]"));
    var dots = Array.prototype.slice.call(root.querySelectorAll("[data-ceb-dot]"));
    if (slides.length < 1) return;

    var wide = window.matchMedia("(min-width: 1000px)");

    function mark(i) {
      slides.forEach(function (s, n) { s.classList.toggle("is-on", n === i); });
      thumbs.forEach(function (t, n) { t.setAttribute("aria-current", n === i ? "true" : "false"); });
      dots.forEach(function (d, n) { d.classList.toggle("is-on", n === i); });
    }

    function nearest() {
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = 0, bestD = Infinity;
      slides.forEach(function (s, i) {
        var c = s.offsetLeft + s.offsetWidth / 2;
        var d = Math.abs(c - mid);
        if (d < bestD) { bestD = d; best = i; }
      });
      return best;
    }

    function show(i) {
      if (wide.matches) { mark(i); return; }
      track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: smooth() });
      mark(i);
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener("click", function () { show(i); });
    });
    track.addEventListener("scroll", function () {
      if (wide.matches) return;
      window.requestAnimationFrame(function () { mark(nearest()); });
    }, { passive: true });

    /* Crossing the breakpoint changes which reading is in force, so the state
       is recomputed rather than left describing the other layout. */
    if (wide.addEventListener) wide.addEventListener("change", function () { mark(wide.matches ? 0 : nearest()); });

    mark(0);
  }

  /* ═══ 2. STICKY BAR ════════════════════════════════════════════════════════
   *
   * Shown once the real purchase button has scrolled out of view, and hidden
   * again when it comes back - IntersectionObserver rather than a scroll
   * handler, so it costs nothing while the page is being read.
   *
   * The button is not a second Add to cart. It submits THE SAME form, so there
   * is one validated code path, one variant and no way to add twice.
   */
  function initSticky(bar) {
    if (bar.__cebStick) return;
    bar.__cebStick = true;

    var formId = bar.getAttribute("data-ceb-form");
    var form = formId ? document.getElementById(formId) : null;
    var anchor = document.querySelector("[data-ceb-atc]");
    if (!form || !anchor) { bar.hidden = true; return; }

    var btn = bar.querySelector("[data-ceb-stick-go]");
    if (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        /* Anything the customer still has to answer is up in the form, so the
           form is brought to them and focused rather than the submit failing
           somewhere they cannot see. checkValidity() is the browser's, over
           the real controls, so it cannot disagree with the form itself. */
        if (!form.checkValidity()) {
          var bad = form.querySelector(":invalid");
          if (bad) {
            bad.scrollIntoView({ block: "center", behavior: smooth() });
            window.setTimeout(function () { try { bad.focus({ preventScroll: true }); } catch (err) { bad.focus(); } }, 320);
            if (form.reportValidity) form.reportValidity();
          }
          return;
        }
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      });
    }

    if (!("IntersectionObserver" in window)) { bar.hidden = true; return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        /* Only once it is above the viewport: a button still below the fold
           has not been scrolled past, it has not been reached. */
        var past = !en.isIntersecting && en.boundingClientRect.top < 0;
        bar.classList.toggle("is-on", past);
      });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ═══ 3. READER PREFERENCE ═════════════════════════════════════════════════
   *
   * The field is a real <select> with a real name, so its answer travels to the
   * cart as a line-item property through the ordinary form post. All this adds
   * is a readable message in place of the browser's default bubble when the
   * merchant has made it required.
   */
  function initPref(form) {
    if (form.__cebPref) return;
    form.__cebPref = true;
    var sel = form.querySelector("[data-ceb-pref]");
    var err = form.querySelector("[data-ceb-pref-err]");
    if (!sel || !err) return;

    function check() {
      var bad = sel.required && !sel.value;
      err.hidden = !bad;
      sel.setAttribute("aria-invalid", bad ? "true" : "false");
      return !bad;
    }
    sel.addEventListener("change", check);
    form.addEventListener("submit", function (e) {
      if (!check()) {
        e.preventDefault();
        sel.scrollIntoView({ block: "center", behavior: smooth() });
        window.setTimeout(function () { try { sel.focus({ preventScroll: true }); } catch (err2) { sel.focus(); } }, 320);
      }
    });
  }

  /* ═══ 4. SECTION CTAs ══════════════════════════════════════════════════════
   *
   * The buttons that move down or back up the page. A plain in-page link would
   * do it, but the header is sticky, so the target has to be offset by its
   * height or it lands under the bar.
   */
  function scrollToId(id) {
    var el = document.getElementById(id);
    if (!el) return false;

    /* scrollIntoView, NOT window.scrollTo.

       This theme does not scroll the window: calmy-scrollbar.js puts html and
       body at overflow: hidden and .page-wrapper is the element that actually
       scrolls, so window.scrollTo moved nothing on a desktop - measured, the
       wrapper's scrollTop stayed at 0 and the page did not move. It worked on
       a phone only because the custom scrollbar is desktop-only.

       scrollIntoView walks up to whatever the real scrolling ancestor is, so
       it is correct on both, and scroll-margin-top is what keeps the target
       clear of the sticky header instead of arithmetic on a scroll offset that
       belongs to the wrong element. */
    var head = document.querySelector(".chead, header");
    var off = head ? Math.round(head.getBoundingClientRect().height) : 0;
    el.style.scrollMarginTop = (off + 12) + "px";
    el.scrollIntoView({ block: "start", behavior: smooth() });
    return true;
  }

  document.addEventListener("click", function (e) {
    var t = e.target.closest && e.target.closest("[data-ceb-goto]");
    if (!t) return;
    var id = t.getAttribute("data-ceb-goto");
    if (!id) return;
    /* A modified click on a real link still behaves like a link. */
    if (t.tagName === "A" && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    if (scrollToId(id)) e.preventDefault();
  });

  /* ═══ 5. NO BUNDLE PROPERTY ON AN E-BOOK ADD ═══════════════════════════════
   *
   * The bundles app embed runs on every product page and writes its selection
   * into the form as properties[__kaching_bundles]. CSS hides its widget here;
   * this makes sure nothing it injected can travel with the order either -
   * checked at submit time, because the app writes the input when an offer is
   * picked, not when the page loads.
   *
   * Scoped to this form. The jewellery page's form is never touched, and no
   * app setting is changed: the widget still works everywhere else.
   */
  function guardBundles(form) {
    if (form.__cebGuard) return;
    form.__cebGuard = true;

    /* THE GUARD IS FOR THE EMBED, NOT FOR A BLOCK THE MERCHANT PLACED.

       Stripping the app's property made sense while the only bundle widget on
       this page was one the embed injected on its own, carrying another
       product's deal. A Kaching app block in this column is the opposite: it
       was added deliberately, at a chosen position, for THIS product - and a
       bundle the customer selects there has to reach the cart, or the widget
       is a control that does nothing.

       Checked on every pass rather than once, because the block can be added,
       moved or removed in the editor while the page is open. */
    function placedByMerchant() {
      return !!document.querySelector(
        '.shopify-app-block kaching-bundles-block, .shopify-app-block [class*="kaching-bundles"]'
      );
    }

    function strip() {
      if (placedByMerchant()) return 0;
      var found = 0;
      form.querySelectorAll('input[name^="properties[__kaching"]').forEach(function (n) {
        n.parentNode.removeChild(n);
        found++;
      });
      return found;
    }

    /* REMOVED AS IT APPEARS, NOT AT SUBMIT TIME.

       Stripping it on submit was not enough: the app writes the input on load
       and adds to the cart through its own fetch, so by the time a submit
       handler ran the property had already been read - measured, an add from
       this page arrived carrying the ring's deal. A MutationObserver takes it
       out the moment it is inserted, so there is no window in which it exists
       to be serialised.

       Scoped to this one form, and only to the app's own __kaching property -
       the reader-preference property and every other field are untouched. */
    strip();
    if (window.MutationObserver) {
      new MutationObserver(strip).observe(form, { childList: true, subtree: true });
    }
    form.addEventListener("submit", strip, true);
  }

  function boot(scope) {
    var root = scope || document;
    root.querySelectorAll("[data-ceb-gallery]").forEach(initGallery);
    root.querySelectorAll("[data-ceb-sticky]").forEach(initSticky);
    root.querySelectorAll("[data-ceb-form-el]").forEach(function (f) { initPref(f); guardBundles(f); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
